import { Injectable } from '@angular/core'
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http'
import { Observable, Subject, throwError } from 'rxjs'
import { urls } from '@constants/urls'
import { catchError } from 'rxjs/operators'
import { AoeUser, AoeUsersResponse } from '../admin/model/aoe-users-response'
import { ChangeOwnerResponse } from '../admin/model/change-owner-response'
import { ChangeOwnerPost } from '../admin/model/change-owner-post'
import { RemoveMaterialResponse } from '../admin/model/remove-material-response'
import { MaterialInfoResponse } from '../admin/model/material-info-response'

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  public users$ = new Subject<AoeUser[]>()
  public materialInfo$ = new Subject<MaterialInfoResponse>()

  constructor(private http: HttpClient) {}

  /**
   * Handles errors.
   * @param {HttpErrorResponse} _error
   * @private
   */
  private handleError(_error: HttpErrorResponse): Observable<never> {
    return throwError('Something bad happened; please try again later.')
  }

  /**
   * Checks if user has admin privileges.
   */
  getAdminStatus(): Observable<HttpResponse<string>> {
    return this.http
      .post(`${urls.backendUrl}/userinfo`, null, {
        observe: 'response',
        responseType: 'text'
      })
      .pipe(catchError(this.handleError))
  }
  /**
   * Updates list of aoe users.
   */
  updateUsers(): void {
    this.http
      .get<AoeUsersResponse>(`${urls.backendUrl}/aoeUsers`)
      .subscribe((usersResponse: AoeUsersResponse) => {
        const users = usersResponse.users
          .sort((a: AoeUser, b: AoeUser) => +a.id - +b.id)
          .map((user: AoeUser) => ({
            ...user,
            fullName: `${user.firstname} ${user.lastname}`
          }))

        this.users$.next(users)
      })
  }

  /**
   * Changes owner of educational material.
   * @param {ChangeOwnerPost} payload
   * @returns {Observable<ChangeOwnerResponse>}
   */
  changeMaterialOwner(payload: ChangeOwnerPost): Observable<ChangeOwnerResponse> {
    return this.http
      .post<ChangeOwnerResponse>(`${urls.backendUrl}/changeUser`, payload)
      .pipe(catchError(this.handleError))
  }

  removeMaterial(materialId: string): Observable<RemoveMaterialResponse> {
    return this.http
      .delete<RemoveMaterialResponse>(`${urls.backendUrl}/removeMaterial/${materialId}`)
      .pipe(catchError(this.handleError))
  }

  updateMaterialInfo(materialId: string): void {
    this.http.get<MaterialInfoResponse>(`${urls.backendUrl}/names/${materialId}`).subscribe(
      (response: MaterialInfoResponse) => this.materialInfo$.next(response),
      () => this.materialInfo$.next(null)
    )
  }
}
